/*** IMPORTS FROM imports-loader ***/
var fetchPolyfill = require("polyfills/fetch");
var classListPolyfill = require("polyfills/classList");

import _ from "underscore";

var HANDLER_METHOD_ENTER = "enter";
var HANDLER_METHOD_UPDATE = "update";
var HANDLER_METHOD_EXIT = "exit";

var HANDLER_METHODS = [HANDLER_METHOD_ENTER, HANDLER_METHOD_UPDATE, HANDLER_METHOD_EXIT];

function emptyHandler() {
    return null;
}

/**
 * @param {object} state
 * @returns {string[]} An array of state ids.
 */
function getPathToState(state) {
    if (!state) {
        return [];
    }

    var path = [state.id];

    while (state.parent) {
        state = state.parent;
        if (state.id) {
            path.unshift(state.id);
        }
    }

    return path;
}

/**
 * @param {object} parent
 * @returns {function} A function that can be used to register a state.
 */
function makeMatcher(parent) {
    return function(stateId, handler, fn) {
        // make sure all the handler methods are present
        _.each(HANDLER_METHODS, function(method) {
            if (!_.isFunction(handler[method])) {
                handler[method] = emptyHandler;
            }
        });

        var state = {
            id: stateId,
            handler: handler,
            parent: parent,
            children: null
        };

        // if a function has been provided to register child states call that now passing this state as the parent.
        if (fn) {
            fn(makeMatcher.call(this, state));
        }

        // index the state under the parent
        parent.children = parent.children || {};
        parent.children[stateId] = state;

        // also index by id
        this._statesById[stateId] = state;
    }.bind(this);
}

/**
 * Schedules and starts the execution of all transitions required to get from stateA to stateB.
 * @param {object} stateA The state being transitioned from.
 * @param {object} stateB The state being transitioned to.
 * @param {string} args
 */
function addTransitions(stateA, stateB, args) {
    stateA = getPathToState(stateA);
    stateB = getPathToState(stateB);

    // while stateA has length and the last entry doesn't match entry with the same index in
    // stateB we need to exit out of entries in stateA.
    while (stateA.length && stateA[stateA.length - 1] !== stateB[stateA.length - 1]) {
        this._transitions.push({
            state: this._statesById[stateA.pop()],
            action: HANDLER_METHOD_EXIT,
            args: args
        });
    }

    for (var i = 0; i < stateB.length; i++) {
        var enterState = this._statesById[stateB[i]];
        var lastKnownArgs = enterState.lastKnownArgs;
        var isDestinationState = i === stateB.length - 1;

        enterState.lastKnownArgs = args;

        if (i > stateA.length - 1) {
            this._transitions.push({
                state: enterState,
                action: HANDLER_METHOD_ENTER,
                args: args
            });
        } else if (isDestinationState && !_.isEqual(args, lastKnownArgs)) {
            // we only want to call update() on the destination state if args are diffeen
            this._transitions.push({
                state: enterState,
                action: HANDLER_METHOD_UPDATE,
                args: args
            });
        }
    }

    if (!this._executingTransitions) {
        this._executingTransitions = true;

        // all transition operations should be async, even the first one
        Promise.resolve().then(executeNextTransition.bind(this));
    }
}

/**
 * Executes the next transition in the queue.
 */
function executeNextTransition() {
    var transition = this._transitions[0];

    if (!transition) {
        this._executingTransitions = false;
        return;
    }

    var shiftTransition = this._transitions.shift.bind(this._transitions);
    var executeNext = executeNextTransition.bind(this);
    var method;

    switch (transition.action) {
        case HANDLER_METHOD_ENTER:
            method = executeEnterTransition;
            break;
        case HANDLER_METHOD_UPDATE:
            method = executeUpdateTransition;
            break;
        case HANDLER_METHOD_EXIT:
            method = executeExitTransition;
            break;
    }

    method = method.bind(this, transition.state, transition);

    method()
        .then(shiftTransition)
        .then(executeNext);
}

/**
 * Executes an 'enter' transition on `state`.
 * @param {object} state
 * @param {object} transition
 * @returns {Promise}
 */
function executeEnterTransition(state, transition) {
    var args = [...transition.args, state.parent.returnValue];
    var result = state.handler[transition.action].apply(state.handler, args);
    var promise = Promise.resolve(result);

    return promise.then(
        function(returnValue) {
            state.returnValue = returnValue;
            this._activeState = state;
        }.bind(this)
    );
}

/**
 * Executes an 'update' transition on `state`.
 * @param {object} state
 * @param {object} transition
 * @returns {Promise}
 */
function executeUpdateTransition(state, transition) {
    var args = [...transition.args, state.returnValue];
    var result = state.handler[transition.action].apply(state.handler, args);
    var promise = Promise.resolve(result);

    return promise.then(
        function() {
            this._activeState = state;
        }.bind(this)
    );
}

/**
 * Exectutes an 'exit' transition on `state`.
 * @param {object} state
 * @param {object} transition
 * @returns {Promise}
 */
function executeExitTransition(state, transition) {
    var args = [...transition.args, state.returnValue];
    var result = state.handler[transition.action].apply(state.handler, args);
    return Promise.resolve(result);
}

/**
 * The StateMachine class used to create the public API.
 * @constructor
 */
function StateMachine() {
    this._statesMap = {
        parent: null,
        children: {}
    };
    this._statesById = {};
    this._activeState = null;
    this._transitions = [];
    this._executingTransitions = false;
}

/**
 * Registers new states.
 * @param {function} fn A function that will be called with one argument, another function
 *                      which can be used to register states.
 * @example
 *
 *  var stater = new StateMachine();
 *
 *  stater.map(function (match) {
 *
 *      match('/', {
 *          enter: function () {},
 *          update: function () {},
 *          exit: function () {}
 *      }, function (match) {
 *
 *          match('foo/', {
 *              enter: function () {},
 *              update: function () {},
 *              exit: function () {}
 *          });
 *
 *      });
 *
 *  });
 *
 */
StateMachine.prototype.map = function(fn) {
    fn(makeMatcher.call(this, this._statesMap));
};

/**
 * Transitions the state machine to a new state.
 * @param {string} stateId
 * @param {...args}
 */
StateMachine.prototype.transitionTo = function(stateId) {
    var destinationState = this._statesById[stateId];
    var args = _.rest(arguments, 1);

    var startState;

    if (this._transitions.length) {
        // if there are transitions pending we want the state that will be active when all transitions have been executed
        startState = _.last(this._transitions).state;
    } else {
        // otherwise we can use the active state (which will be null the first time update() is called)
        startState = this._activeState;
    }

    addTransitions.call(this, startState, destinationState, args);
};

export default StateMachine;

